import React, { useState } from "react";
import Modal from "./Modal.tsx";
import StyledButton from "./Button.tsx";
import {
  ModalInput,
  ModalLabel,
  ModalButtonRow,
  CancelButton,
  SmallText,
  ErrorMessage,
} from "./ModalFormComponents.tsx";
import { createCategory } from "../services/categories.service.ts";
import { parseApiError } from "../utils/errorHandler.ts";

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateCategoryModal({
  isOpen,
  onClose,
}: CreateCategoryModalProps) {
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setCategoryName("");
    setCategoryDescription("");
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = (): boolean => {
    setError(null);

    if (!categoryName.trim()) {
      setError("Kategori navn er påkrævet");
      return false;
    }

    return true;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    setCreating(true);

    try {
      await createCategory({
        name: categoryName.trim(),
        description: categoryDescription.trim() || undefined,
      });

      handleClose();
      window.location.reload();
    } catch (err: unknown) {
      setError(parseApiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Opret ny kategori">
      <SmallText>
        Opret en ny kategori
      </SmallText>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ModalLabel>Navn på kategori *</ModalLabel>
      <ModalInput
        placeholder="Navn på kategorien"
        value={categoryName}
        onChange={(e) => setCategoryName(e.target.value)}
      />

      <ModalLabel>Beskrivelse</ModalLabel>
      <ModalInput
        placeholder="Beskrivelse af kategorien (valgfrit)"
        value={categoryDescription}
        onChange={(e) => setCategoryDescription(e.target.value)}
      />

      <ModalButtonRow>
        <CancelButton onClick={handleClose} disabled={creating}>
          Annuller
        </CancelButton>
        <StyledButton onClick={handleCreate} disabled={creating}>
          {creating ? "Opretter..." : "Opret"}
        </StyledButton>
      </ModalButtonRow>
    </Modal>
  );
}
